import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { useMemo } from 'react';
import {
  SelectArrayInput,
  TextField,
  TopToolbar,
  useListContext,
  useRecordContext,
} from 'react-admin';

import {
  GetB2bCampaignsPayoutsOneResponse,
  GetB2bCampaignsPayoutsQuery,
  getB2bCampaignsPayoutsSortColumns,
} from '@Common/types/b2b/campaigns';
import { GetB2bCampaignOneResponse } from '@Common/types/bo/b2b/campaigns';
import { ZodMdlrPaymentStatus } from '@Common/types/modulr';

import { contractCampaignActionHandlerChoices } from '@sh4res/bo-common/choices/contracts';
import { b2bPayoutsStatusChoices } from '@sh4res/bo-common/choices/payouts';
import {
  DatagridTyped,
  FilterButton,
  List,
  ServerExportButton,
  ShowSection,
} from '@sh4res/bo-common/components';
import { PayoutTransferIdField } from '@sh4res/bo-common/components/B2bCampaigns/payouts/PayoutTransferIdField';
import { SelectColumnsButton } from '@sh4res/bo-common/components/SelectColumnsButton';
import {
  CurrencyField,
  IdField,
  SelectChipField,
  TextArrayInput,
  UTCDateField,
} from '@sh4res/bo-common/fields';
import { DateInput } from '@sh4res/bo-common/fields/DateInput';
import { ResourceBasePath, useResourceBasePath, useSelectedColumns } from '@sh4res/bo-common/hooks';
import { ColumnList, RaSort } from '@sh4res/bo-common/utils';
import { isBoExternal } from '@sh4res/bo-common/utils/env';

const testId = 'BoExternalB2bCampaignPayoutsTab';

const preference = 'b2b_campaigns.payouts.columns';

/**
 * the transfer status is shown and filtered as the technical value stored in the database, so
 * support can tell `cancelled` from `void` — the translated labels collapse both to "Annulé".
 */
const rawTransferStatusChoices = ZodMdlrPaymentStatus.options.map((status) => ({
  id: status,
  name: status,
}));

type PayoutListRow = Omit<GetB2bCampaignsPayoutsOneResponse, 'tax_info'>;

const payoutsListColumns = (resourceBasePath: ResourceBasePath): ColumnList<PayoutListRow> => ({
  id: (
    <IdField reference={`${resourceBasePath}campaigns/payouts`} data-testid={`${testId}_IdField`} />
  ),
  b2b_campaign_id: (
    <IdField reference={`${resourceBasePath}campaigns`} data-testid={`${testId}_CampaignIdField`} />
  ),
  b2b_company_id: (
    <IdField reference={`${resourceBasePath}companies`} data-testid={`${testId}_CompanyIdField`} />
  ),
  b2b_employee_id: (
    <IdField reference={`${resourceBasePath}employees`} data-testid={`${testId}_EmployeeIdField`} />
  ),
  lastname: <TextField data-testid={`${testId}_LastnameField`} />,
  firstname: <TextField data-testid={`${testId}_FirstnameField`} />,
  savings_funds_request_id: (
    <IdField
      reference={`${resourceBasePath}savings_funds_requests`}
      data-testid={`${testId}_SavingsFundsRequestIdField`}
    />
  ),
  payout_by: (
    <SelectChipField
      choices={contractCampaignActionHandlerChoices}
      data-testid={`${testId}_PayoutByField`}
    />
  ),
  status: (
    <SelectChipField choices={b2bPayoutsStatusChoices} data-testid={`${testId}_StatusField`} />
  ),
  user_currency: <TextField data-testid={`${testId}_UserCurrencyField`} />,
  user_currency_amount: <CurrencyField currency="user_currency" testId={`${testId}_AmountField`} />,
  total_gross_amount: (
    <CurrencyField currency="user_currency" testId={`${testId}_GrossAmountField`} />
  ),
  tax_amount: <CurrencyField currency="user_currency" testId={`${testId}_TaxAmountField`} />,
  transfer_id: <PayoutTransferIdField data-testid={`${testId}_TransferIdField`} />,
  transfer_mdlr_status: <TextField data-testid={`${testId}_TransferMdlrStatusField`} />,
  transfer_amount: (
    <CurrencyField currency="user_currency" testId={`${testId}_TransferAmountField`} />
  ),
  transfer_created_at: (
    <UTCDateField showTime={!isBoExternal()} data-testid={`${testId}_TransferCreatedAtField`} />
  ),
  transfer_executed_at: (
    <UTCDateField showTime={!isBoExternal()} data-testid={`${testId}_TransferExecutedAtField`} />
  ),
  created_at: <UTCDateField showTime={!isBoExternal()} data-testid={`${testId}_CreatedAtField`} />,
  updated_at: <UTCDateField showTime={!isBoExternal()} data-testid={`${testId}_UpdatedAtField`} />,
});

/** Also the columns of the export */
const defaultColumns: (keyof PayoutListRow)[] = [
  'id',
  'b2b_employee_id',
  'lastname',
  'firstname',
  'total_gross_amount',
  'user_currency_amount',
  'transfer_id',
  'transfer_mdlr_status',
];

const sortableColumns = getB2bCampaignsPayoutsSortColumns.filter(
  (column): column is keyof PayoutListRow => column !== 'tax_info',
);

const defaultSort: RaSort<GetB2bCampaignsPayoutsQuery> = { field: 'created_at', order: 'DESC' };

const payoutsFilters = [
  <SelectArrayInput
    key="status"
    source="status"
    choices={b2bPayoutsStatusChoices}
    data-testid={`${testId}_StatusFilterInput`}
  />,
  <TextArrayInput
    key="b2b_employee_id"
    source="b2b_employee_id"
    asNumber
    testId={`${testId}_EmployeeIdFilterInput`}
  />,
  <TextArrayInput key="id" source="id" asNumber testId={`${testId}_IdFilterInput`} />,
  <TextArrayInput key="lastname" source="lastname" testId={`${testId}_LastnameFilterInput`} />,
  <TextArrayInput key="firstname" source="firstname" testId={`${testId}_FirstnameFilterInput`} />,
  <TextArrayInput
    key="transfer_id"
    source="transfer_id"
    testId={`${testId}_TransferIdFilterInput`}
  />,
  <SelectArrayInput
    key="transfer_mdlr_status"
    source="transfer_mdlr_status"
    choices={rawTransferStatusChoices}
    data-testid={`${testId}_TransferMdlrStatusFilterInput`}
  />,
  <DateInput
    key="from_created_at"
    source="from_created_at"
    data-testid={`${testId}_FromCreatedAtFilterInput`}
  />,
  <DateInput
    key="to_created_at"
    source="to_created_at"
    data-testid={`${testId}_ToCreatedAtFilterInput`}
  />,
];

const ActionsItem = styled(Box)`
  margin: ${({ theme }) => theme.spacing(0, 0, 0, 0.5)};
`;

const PayoutsExportButton = ({
  campaignId,
  'data-testid': dataTestId,
}: {
  campaignId: number;
  'data-testid': string;
}) => {
  const resourceBasePath = useResourceBasePath();
  const { filterValues, sort } = useListContext();

  return (
    <ServerExportButton
      resource={`${resourceBasePath}campaigns/payouts`}
      getBody={() => ({
        kind: 'campaign_payouts',
        b2b_campaign_id: campaignId,
        query: {
          filter: JSON.stringify(filterValues),
          sort: JSON.stringify([sort.field, sort.order]),
        },
      })}
      data-testid={dataTestId}
    />
  );
};

const PayoutsActions = ({
  campaignId,
  columns,
}: {
  campaignId: number;
  columns: ColumnList<PayoutListRow>;
}) => (
  <TopToolbar>
    <ActionsItem>
      <FilterButton data-testid={`${testId}_FilterButton`} />
    </ActionsItem>
    <ActionsItem>
      <SelectColumnsButton
        preference={preference}
        columns={columns}
        defaultColumns={defaultColumns}
        testId={`${testId}_SelectColumnsButton`}
      />
    </ActionsItem>
    <ActionsItem>
      <PayoutsExportButton campaignId={campaignId} data-testid={`${testId}_ExportButton`} />
    </ActionsItem>
  </TopToolbar>
);

const PayoutsSection = ({ campaignId }: { campaignId: number }) => {
  const resourceBasePath = useResourceBasePath();
  const columns = useMemo(() => payoutsListColumns(resourceBasePath), [resourceBasePath]);

  const displayedColumns = useSelectedColumns<PayoutListRow>({
    preferences: preference,
    defaultColumns,
  });

  return (
    <ShowSection label="resources.bo_common.b2b.campaigns.tabs.payouts.label" sx={{ mb: '90px' }}>
      <List
        resource={`${resourceBasePath}campaigns/payouts`}
        storeKey="b2b_campaigns.payouts"
        title=" "
        filter={{ b2b_campaign_id: campaignId }}
        filters={payoutsFilters}
        sort={defaultSort}
        exporter={false}
        actions={<PayoutsActions campaignId={campaignId} columns={columns} />}
        disableSyncWithLocation
        empty={false}
        sx={{ '& .RaList-main': { minWidth: 0 } }}>
        <DatagridTyped<PayoutListRow>
          rowClick="show"
          bulkActionButtons={false}
          showCard
          sortableColumns={sortableColumns}
          displayedColumns={displayedColumns}
          columnRegistry={columns}
        />
      </List>
    </ShowSection>
  );
};

export const B2bCampaignPayoutsTab = () => {
  const campaign = useRecordContext<GetB2bCampaignOneResponse>();

  if (!campaign) {
    return null;
  }

  return <PayoutsSection campaignId={campaign.id} />;
};
